import { Pagination } from '@mantine/core';
import { Alert } from 'src/design/atoms/Alert';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Drawer } from 'src/design/molecules/Drawer';
import { Table } from 'src/design/molecules/Table';
import { IconInfoCircle } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import {
  AZURE_GROUP_SYNC_LOG_PAGE_SIZE as PAGE_SIZE,
  useAzureGroupSyncLogs,
} from 'src/api/queries/admin/useAzureGroupSync';
import { formatDateTime } from 'src/utils';

interface AzureGroupSyncLogsDrawerProps {
  opened: boolean;
  onClose: () => void;
}

export const AzureGroupSyncLogsDrawer: React.FC<
  AzureGroupSyncLogsDrawerProps
> = ({ opened, onClose }) => {
  const [page, setPage] = useState(1);
  const logsQuery = useAzureGroupSyncLogs(page, opened);
  const logs = logsQuery.data?.logs ?? [];
  const totalCount = logsQuery.data?.totalCount ?? 0;
  const retentionDays = logsQuery.data?.retentionDays ?? null;
  const loading = logsQuery.isFetching;

  // Reset page when drawer opens
  useEffect(() => {
    if (opened) {
      setPage(1);
    }
  }, [opened]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Azure Group Sync Logs"
      position="right"
      size="70%"
    >
      <Stack pos="relative" mih={200}>
        <LoadingOverlay visible={loading} />

        {retentionDays !== null && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            Logs are retained for {retentionDays} days. Older entries are
            automatically removed.
          </Alert>
        )}

        {logs.length === 0 && !loading && (
          <Text c="dimmed" ta="center" mt="lg">
            No sync logs found.
          </Text>
        )}

        {logs.length > 0 && (
          <>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Time</Table.Th>
                  <Table.Th>User Email</Table.Th>
                  <Table.Th>Operation</Table.Th>
                  <Table.Th>Azure Group ID</Table.Th>
                  <Table.Th>Result</Table.Th>
                  <Table.Th>Error</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {logs.map((log) => (
                  <Table.Tr key={log.id}>
                    <Table.Td>
                      <Text size="xs">{formatDateTime(log.createdAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{log.userEmail || '—'}</Text>
                    </Table.Td>
                    <Table.Td>{log.operation}</Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {log.azureGroupId || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={log.succeeded ? 'green' : 'red'} size="sm">
                        {log.succeeded ? 'OK' : 'FAIL'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {log.errorMessage && (
                        <Text size="xs" c="red" lineClamp={2}>
                          {log.errorMessage}
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                />
              </Group>
            )}
          </>
        )}
      </Stack>
    </Drawer>
  );
};
